
import {
  computed, defineComponent, onBeforeMount, reactive,
} from 'vue';
import { useAppBase } from '@/core/composables/AppBase';
import { IPedido } from '@/models/Entidades/IPedido';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import SelecionarDataInicialFinal from '@/core/components/UI/SelecionarDataInicialFinal.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import ServicoPedido from '@/servicos/ServicoPedido';
import { IDTOPedidosForcaVendasFiltros } from '@/models/DTO/ForcaVendas/IDTOPedidosForcaVendasFiltros';
import storeApp from '@/store/storeApp';
import router from '@/router';

export default defineComponent({
  name: 'PedidoAcompanhamento',
  components: {
    ComunicacaoApi,
    SelecionarDataInicialFinal,
  },
  setup() {
    const {
      appBase,
    } = useAppBase();

    const servicoPedido = new ServicoPedido();

    const state = reactive({
      pedidos: [] as IPedido[],
      filtros: {} as IDTOPedidosForcaVendasFiltros,
      pesquisa: '',
    });

    function obterQuantidadeItens(pedido: IPedido) {
      if (UtilitarioGeral.validaLista(pedido.itens)) {
        return pedido.itens.length;
      }

      return 0;
    }

    function removerAcentos(valor: string) {
      if (!valor) return '';

      return valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    function obterTextoPesquisaPedido(pedido: IPedido) {
      return [
        pedido.codigo,
        pedido.numeroIdentificacao,
        pedido.numeroErp,
        servicoPedido.obterDescricaoStatus(pedido.status),
        pedido.detalhesFormaPagamento,
      ].join(' ');
    }

    const pedidosFiltrados = computed(() => {
      if (!UtilitarioGeral.valorValido(state.pesquisa)) {
        return state.pedidos;
      }

      const pesquisa = removerAcentos(state.pesquisa).toLowerCase();

      return state.pedidos.filter((pedido) => {
        const textoPesquisa = removerAcentos(obterTextoPesquisaPedido(pedido)).toLowerCase();

        return textoPesquisa.includes(pesquisa);
      });
    });

    const quantidadeItensPedidos = computed(() => pedidosFiltrados.value.reduce((total, pedido) => total + obterQuantidadeItens(pedido), 0));

    const valorTotalPedidos = computed(() => pedidosFiltrados.value.reduce((total, pedido) => total + (pedido.valorTotal || 0), 0));

    function limparDados() {
      state.pedidos = [];
      state.pesquisa = '';
      state.filtros = {} as IDTOPedidosForcaVendasFiltros;
      state.filtros.codigoVendedor = storeApp.state.codigoVendedor;
      state.filtros.codigosClientes = [];
      state.filtros.dataInicial = UtilitarioData.obterPrimeiroDiaMesAtual();
      state.filtros.dataFinal = UtilitarioData.obterUltimoDiaMesAtual();
    }

    async function buscarPedidos() {
      try {
        appBase.carregando = true;

        state.filtros.codigoVendedor = storeApp.state.codigoVendedor;
        state.filtros.codigosClientes = [];

        state.pedidos = await servicoPedido.pedidosForcaVendas(state.filtros);
      } catch {
        state.pedidos = [];
      } finally {
        appBase.carregando = false;
      }
    }

    onBeforeMount(async () => {
      limparDados();

      await buscarPedidos();
    });

    function abrirPedido(pedido: IPedido) {
      if (pedido.codigo > 0) {
        router.push({
          name: 'ForcaVendasPedidoCodigo',
          params: {
            codigo: pedido.codigo,
          },
        });
      }
    }

    function voltarTela() {
      router.push({
        name: 'Dashboard',
      });
    }

    function somenteNumeros(valor: string) {
      if (!valor) return '';

      return valor.replace(/\D/g, '');
    }

    function formatarMoeda(valor: number) {
      return (valor || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function formatarData(data: string) {
      if (!UtilitarioGeral.valorValido(data)) {
        return '-';
      }

      const dataConvertida = new Date(data);

      if (Number.isNaN(dataConvertida.getTime())) {
        return data;
      }

      return dataConvertida.toLocaleDateString('pt-BR');
    }

    return {
      appBase,
      state,
      pedidosFiltrados,
      quantidadeItensPedidos,
      valorTotalPedidos,
      buscarPedidos,
      abrirPedido,
      voltarTela,
      obterQuantidadeItens,
      somenteNumeros,
      formatarMoeda,
      formatarData,
      UtilitarioGeral,
      UtilitarioMascara,
      servicoPedido,
    };
  },
});
